import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ArrowUp, Loader2 } from "lucide-react";
import { avatarFor, timeAgo } from "@/lib/utils-tg";
import { useAuth } from "@/context/AuthContext";

export default function Thread({ objectType, objectId }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/threads/${objectType}/${objectId}`);
      setMsgs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [objectType, objectId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/threads/${objectType}/${objectId}`, { text: t });
      setMsgs((prev) => [...prev, data]);
      setText("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="thread-component">
      <div className="flex-1 overflow-y-auto px-1 py-2 space-y-3">
        {loading && (
          <div className="text-sm text-slate-400 text-center py-6">Loading thread…</div>
        )}
        {!loading && msgs.length === 0 && (
          <div className="text-sm text-slate-400 text-center py-6">
            No messages yet. Start the conversation.
          </div>
        )}
        {msgs.map((m) => {
          const me = m.user_id === user?.id;
          return (
            <div key={m.id} className={`flex gap-2 ${me ? "flex-row-reverse" : ""}`} data-testid="thread-message">
              <img
                src={avatarFor(m.user_name || "U")}
                alt=""
                className="h-7 w-7 rounded-full object-cover shrink-0"
              />
              <div className={`max-w-[80%] ${me ? "items-end" : "items-start"} flex flex-col`}>
                <div className="flex items-center gap-2 mb-0.5 px-0.5">
                  <span className="text-xs font-semibold text-slate-700">{m.user_name}</span>
                  <span className="text-[10px] text-slate-400">{timeAgo(m.created_at)}</span>
                </div>
                <div
                  className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                    me
                      ? "bg-[#16A34A] text-white rounded-br-md"
                      : "bg-slate-100 text-slate-800 rounded-bl-md"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="border-t border-slate-200 p-2 mt-2">
        <div className="flex items-center gap-2 bg-slate-50 rounded-2xl pl-3 pr-1 py-1">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Write a message…"
            disabled={busy}
            className="flex-1 bg-transparent border-0 focus:ring-0 focus:outline-none text-sm py-2"
            data-testid="thread-input"
          />
          <button
            type="button"
            onClick={send}
            disabled={busy || !text.trim()}
            className="h-8 w-8 rounded-full bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-50 text-white flex items-center justify-center transition-all"
            data-testid="thread-send"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
